// src/context/CalculationContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useBioData } from './BioDataContext';
import { useSettings } from './SettingsContext';
import { useScenarios } from './ScenarioContext';
import { useUser } from './UserContext';
import { 
  calculateSalaryData,
  calculateHigh3Salary,
  calculatePensionData,
  calculateTspData,
  calculateNpvData,
  calculateEstimatedSalary,
  calculateSocialSecurity, // Added this import
  calculateAdditionalIncomeData // Added for additional income
} from '../utils/calculationUtils';
import { generateCsvData, downloadCsv } from '../utils/exportUtils';
// Rate limiting removed - moved to admin app

const CalculationContext = createContext();

export function useCalculations() {
  return useContext(CalculationContext);
}

export function CalculationProvider({ children }) {
  const { bioData } = useBioData();
  const { globalSettings } = useSettings();
  const { scenarios } = useScenarios();
  const { user } = useUser();
  const currentYear = new Date().getFullYear();
  
  // Calculation results
  const [calculationResults, setCalculationResults] = useState({
    scenarioResults: {},
    socialSecurityData: {}, // Initialize as an empty object
    socialSecurityAt62Raw: 0 // Store the specific raw value for age 62
  });

  // Effect to calculate Social Security benefits when bioData or globalSettings change
  useEffect(() => {
    // Removed problematic logging from useEffect per DEBUG_LOG.md rules

    if (bioData.dateOfBirth && bioData.pia && bioData.ssRetirementAge) {
      const birthYear = new Date(bioData.dateOfBirth).getFullYear();
      // ssRetirementAge is an age, convert to year by adding to birthYear
      const socialSecurityStartYear = birthYear + bioData.ssRetirementAge;
      const deathYear = bioData.deathDate ? new Date(bioData.deathDate).getFullYear() : null;
      const projectionEndYear = globalSettings.endPensionYear;

      // Removed problematic logging from useEffect per DEBUG_LOG.md rules

      const ssData = calculateSocialSecurity(
        birthYear,
        socialSecurityStartYear, // This is the year SS benefits begin, not necessarily FERS retirement year
        bioData.pia,
        deathYear,
        projectionEndYear,
        globalSettings.ssCola, // Social Security COLA rate
        globalSettings.showSsCola // Apply Social Security COLA flag
      );

      // Removed problematic logging from useEffect per DEBUG_LOG.md rules

      // Also, specifically calculate and store the raw SS amount at age 62 for supplement calculations
      // Assuming PIA is the benefit at Full Retirement Age (FRA)
      // We need to adjust it if claiming at 62, if FRA is different.
      // For simplicity here, if ssRetirementAge is 62, pia *is* the value at 62 (after adjustments).
      // If a more precise calculation for supplement is needed, this might need refinement based on FRA.
      let ssAt62 = 0;
      if (ssData && ssData[birthYear + 62]) {
        ssAt62 = ssData[birthYear + 62];
      } else {
        // Fallback: calculate it if not directly available (e.g., if user claims after 62)
        // This requires knowing the PIA is for FRA and applying early claiming reduction.
        // For now, we'll rely on ssData containing it or a simplified estimate.
        // A proper calculation would use getSocialSecurityAdjustmentFactor from calculationUtils.
        // This is a placeholder if ssData doesn't directly provide the value for age 62.
        const tempSsDataAt62 = calculateSocialSecurity(
          birthYear,
          birthYear + 62, // Forcing calculation at age 62
          bioData.pia,
          deathYear, 
          projectionEndYear,
          globalSettings.ssCola, // Social Security COLA rate
          globalSettings.showSsCola // Apply Social Security COLA flag
        );
        ssAt62 = tempSsDataAt62[birthYear + 62] || 0;
      }

      // Removed problematic logging from useEffect per DEBUG_LOG.md rules

      // Convert SS data object to array aligned with projection years
      // ssData is like {2047: 35585, 2048: 35585, ...}
      // We need to convert this to an array aligned with projection years starting from currentYear
      const ssDataArray = [];
      const endYear = globalSettings.endPensionYear || (currentYear + 50);
      
      for (let year = currentYear; year <= endYear; year++) {
        ssDataArray.push(ssData[year] || 0);
      }
      
      // Removed problematic logging from useEffect per DEBUG_LOG.md rules
      
      // Call the same handler that manual calculations use
      handleSocialSecurityCalculation({
        benefits: ssDataArray,
        rawBenefitAt62: ssAt62
      });

      // Removed problematic logging from useEffect per DEBUG_LOG.md rules

    } else {
      // Clear SS data if essential bioData is missing
      setCalculationResults(prevResults => ({
        ...prevResults,
        socialSecurityData: {}, // Clear to an empty object
        socialSecurityAt62Raw: 0
      }));
      // console.log('[CalcContext] SS useEffect - Missing bioData, clearing SS.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bioData.dateOfBirth, bioData.pia, bioData.ssRetirementAge, bioData.deathDate, globalSettings.endPensionYear]);

  // Calculate Social Security Supplement amounts
  const calculateSocialSecuritySupplement = (years, scenario, birthYear, socialSecurityAt62) => {

    // Supplement only applies to FERS Special, requires birth year and SS@62 data.
    if (!birthYear || !scenario || scenario.pensionType !== 'special' || !socialSecurityAt62) {
      return Array(years.length).fill(0);
    }
    
    const { retirementYear, yearsOfService } = scenario;
    
    // Calculate the age the person turns 62
    const age62Year = birthYear + 62;
    
    // The supplement formula is: (YOS / 40) * SS at age 62
    const supplementFactor = yearsOfService / 40;
    const supplementAmount = socialSecurityAt62 * supplementFactor;

    const results = years.map(year => {
      // The supplement is paid from retirement until age 62
      if (year >= retirementYear && year < age62Year) {
        return supplementAmount;
      }
      return 0;
    });
    return results;
  };

  // Calculate all data for a scenario
  const calculateScenario = useCallback(async (scenario) => {
    try {
      if (!bioData || !globalSettings) {
        console.error('Missing required data for calculation');
        return { error: true, message: 'Missing required data for calculation' };
      }

      // Rate limiting removed - calculations are now unlimited for better user experience
    
    // Get scenario-specific settings or use defaults
    const {
      retirementYear = currentYear + (65 - (bioData.age || 0)),
      yearsOfService = 30,
      withdrawalRate = 0.04,
      tspContribution = 5,
      agencyMatch = 5,
      includeColaForSalary = true,
      promotions = [],
      pensionType = 'immediate',
      departureYear = null,
      deferredRetirementAge = null,
    } = scenario;
    
    // console.log('[calculateScenario] Recalculating scenario:', scenario.id);
    // console.log('[calculateScenario] Additional incomes:', scenarios[0]?.additionalIncomes);
    
    // Get DOB for FERS Supplement calculations
    const birthYear = bioData.dateOfBirth ? new Date(bioData.dateOfBirth).getFullYear() : null;
    
    // For deferred retirement, calculate the actual retirement year based on deferred retirement age
    let effectiveRetirementYear = retirementYear;
    if (pensionType === 'deferred' && birthYear && deferredRetirementAge) {
      effectiveRetirementYear = birthYear + deferredRetirementAge;
    }
    
    // Generate year range
    const years = [];
    for (let year = currentYear; year <= globalSettings.endPensionYear; year++) {
      years.push(year);
    }
    
    // Calculate salary data - now returns object with salaries, stepProgression, and gradeProgression
    const salaryResult = calculateSalaryData(
      years, 
      bioData.currentGrade, 
      bioData.currentStep, 
      bioData.locality, 
      pensionType === 'deferred' ? effectiveRetirementYear : retirementYear,
      promotions,
      includeColaForSalary || globalSettings.applyColaWhileEmployed,
      includeColaForSalary ? globalSettings.annualCola : globalSettings.colaWhileEmployed,
      pensionType === 'deferred' ? departureYear : null,
      globalSettings.showColaWhileEmployed // Pass the COLA toggle setting
    );
    
    // Extract the arrays from the result
    const salaryData = salaryResult.salaries;
    const stepProgression = salaryResult.stepProgression;
    const gradeProgression = salaryResult.gradeProgression;
    
    // Get high-3 for pension calculation
    let high3Data;
    
    if (pensionType === 'deferred' && scenario.high3SalaryAtSeparation) {
      // For deferred retirement, use the high-3 provided directly in the former employee fields
      high3Data = scenario.high3SalaryAtSeparation;
      // console.log(`[${scenario.id}] Using provided high-3 for deferred retirement:`, high3Data);
    } else {
      // For other pension types, calculate from salary data
      high3Data = calculateHigh3Salary(salaryData);
    }
    
    // Log step progression for debugging
    // console.log(`[${scenario.id}] Step progression for first 5 years:`, stepProgression.slice(0, 5));
    
    // Calculate pension data
    const pensionData = calculatePensionData(
      salaryData, 
      years, 
      birthYear, // yearOfBirth 
      pensionType === 'deferred' ? effectiveRetirementYear : retirementYear,
      null, // pensionMultiplier (not used directly in the function)
      pensionType === 'deferred' ? 
        (scenario.yearsOfServiceAtDeparture || yearsOfService || 0) : 
        (yearsOfService || 0),
      pensionType, // retirementType 
      calculationResults.socialSecurityData, // socialSecurityData
      bioData,
      high3Data, // estimatedHighThree
      departureYear,
      scenario.deferredRetirementAge, // Pass the deferred retirement age
      globalSettings.showAnnualCola // Pass the COLA toggle setting
    );
    
    // Calculate TSP data
    // Verify the current TSP balance is valid
    if (bioData.currentTspBalance === undefined || bioData.currentTspBalance === null) {
      console.warn(`[${scenario.id}] ALERT: currentTspBalance is ${bioData.currentTspBalance}`);
    }
    
    // console.log(`[${scenario.id}] Calculating TSP data with:`, {
    //   currentBalance: bioData.currentTspBalance,
    //   currentBalanceType: typeof bioData.currentTspBalance,
    //   hasValidBalance: bioData.currentTspBalance > 0,
    //   currentGrade: bioData.currentGrade,
    //   retirementYear: pensionType === 'deferred' ? effectiveRetirementYear : retirementYear,
    //   withdrawalRate,
    //   preGrowth: globalSettings.preRetirementGrowth,
    //   postGrowth: globalSettings.postRetirementGrowth,
    //   departureYear: pensionType === 'deferred' ? departureYear : null,
    //   tspContribution,
    //   agencyMatch
    // });


    // Perform the actual calculation
    const tspData = calculateTspData(
      years, 
      bioData.currentTspBalance || 100000, // Force a minimum balance for testing if needed
      bioData.currentGrade, 
      pensionType === 'deferred' ? effectiveRetirementYear : retirementYear,
      withdrawalRate,
      globalSettings.preRetirementGrowth,
      globalSettings.postRetirementGrowth,
      pensionType === 'deferred' ? departureYear : null,
      tspContribution,
      agencyMatch,
      bioData,
      scenario.tspWithdrawalStartAge
    );
    
    // console.log(`[${scenario.id}] TSP data calculation result:`, {
    //   years: years.length,
    //   hasData: tspData && tspData.length > 0,
    //   minValue: Math.min(...tspData.filter(v => v !== null && v !== undefined)),
    //   maxValue: Math.max(...tspData.filter(v => v !== null && v !== undefined)),
    //   firstFewValues: tspData.slice(0, 3),
    //   lastFewValues: tspData.slice(-3)
    // });
    
    // Calculate Additional Income Data
    // For all scenarios, use the first scenario's additionalIncomes but filter by includedIncomes
    let scenarioIncomes = [];
    
    if (scenarios[0] && scenarios[0].additionalIncomes) {
      // For all scenarios, use the first scenario's additionalIncomes but filter by includedIncomes
      scenarioIncomes = scenarios[0].additionalIncomes.filter(income => {
        // For the first scenario, include all incomes that don't have includedIncomes or are explicitly included
        if (scenario.id === scenarios[0].id) {
          // If no includedIncomes array exists, include all (backward compatibility)
          if (!scenario.includedIncomes) {
            return true;
          }
          // If includedIncomes array exists, check if this income is included
          return scenario.includedIncomes.includes(income.id);
        } else {
          // For other scenarios, only include if explicitly included
          if (!scenario.includedIncomes) {
            return false;
          }
          return scenario.includedIncomes.includes(income.id);
        }
      });
    }
    
    const additionalIncomeData = calculateAdditionalIncomeData(years, scenarioIncomes);

    // Calculate Social Security Supplement
    const supplementData = calculateSocialSecuritySupplement(
      years, 
      scenario,
      birthYear,
      calculationResults.socialSecurityAt62Raw // Use the dedicated raw value
    );
    
    // Get GS information for tooltips using the stepProgression and gradeProgression arrays
    const gsInfo = {};
    years.forEach((year, index) => {
      if (year < retirementYear) {
        // Use the step and grade progression arrays for accurate information
        const currentStep = stepProgression[index];
        const currentGrade = gradeProgression[index];
        
        gsInfo[year] = {
          grade: currentGrade,
          step: currentStep
        };
      }
    });
    
    // Calculate NPV data with all income sources
    const npvData = calculateNpvData(
      years, 
      salaryData, 
      pensionData, 
      tspData, // Still pass as tspData to the calculation function
      withdrawalRate,
      globalSettings.discountRate,
      globalSettings.taxRate,
      globalSettings.showAfterTax,
      supplementData,
      calculationResults.socialSecurityData,
      additionalIncomeData // Pass additional income data here
    );
    
    return {
      years,
      salaryData,
      pensionData,
      tspBalanceData: tspData, // Rename to match what ResultsTable expects
      socialSecurityData: calculationResults.socialSecurityData, // Add SS data here
      supplementData,
      additionalIncomeData, // Add additional income data to results
      npvData,
      high3Salary: high3Data, // Renamed from high3Data
      gsInfo,
      stepProgression, // Add step progression for UI display
      gradeProgression // Add grade progression for UI display
    };
    } catch (error) {
      console.error('Error in calculateScenario:', error);
      return { 
        error: true, 
        message: error.message || 'Calculation failed',
        years: [],
        salaryData: [],
        pensionData: [],
        tspBalanceData: []
      };
    }
  }, [bioData, globalSettings, calculationResults.socialSecurityData, calculationResults.socialSecurityAt62Raw, currentYear, scenarios, user]);

  // Estimate current salary for a scenario
  const estimatedSalaryForScenario = (scenario) => {
    return calculateEstimatedSalary(
      bioData.currentGrade, 
      bioData.currentStep, 
      bioData.locality
    );
  };

  const handleDownloadTable = () => {
    const csvContent = generateCsvData(
      calculationResults.scenarioResults,
      scenarios,
      globalSettings,
      calculationResults.socialSecurityData
    );
    downloadCsv(csvContent);
  };

  // Handle Social Security calculation results
  const handleSocialSecurityCalculation = useCallback((data) => {
    // data is now { benefits: [...], rawBenefitAt62: ... }
    setCalculationResults(prev => ({
      ...prev,
      socialSecurityData: data.benefits || [], // Store the array
      socialSecurityAt62Raw: data.rawBenefitAt62 || 0 // Store the raw value
    }));
  }, []);

  // Update calculations when dependencies change
  useEffect(() => {
    // Calculate for all scenarios when any dependencies change
    const calculateAllScenarios = async () => {
      const newResults = {};
      
      // Calculate each scenario and await the results
      for (const scenario of scenarios) {
        newResults[scenario.id] = await calculateScenario(scenario);
      }
      
      setCalculationResults(prev => {
        return {
          ...prev,
          scenarioResults: newResults,
          // We need to persist the SS data between scenario calculations
          socialSecurityData: prev.socialSecurityData, 
          socialSecurityAt62Raw: prev.socialSecurityAt62Raw
        };
      });
    };
    
    calculateAllScenarios();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarios, bioData, globalSettings, currentYear]); // Remove calculateScenario to break circular dependency

  const value = {
    calculationResults,
    calculateScenario,
    estimatedSalaryForScenario,
    handleDownloadTable,
    handleSocialSecurityCalculation
  };

  return (
    <CalculationContext.Provider value={value}>
      {children}
    </CalculationContext.Provider>
  );
}